/**
 * @module survey_types
 */
import { createReducer, type PayloadAction } from '@reduxjs/toolkit';
import { surveyTypeActions } from '~/behaviors/actions/survey_type.actions';
import { metaActions } from '~/behaviors/actions/meta.actions';
import { categoryActions } from '~/behaviors/actions/category.actions';
import { accountActions } from '~/behaviors/actions/account.actions';

export interface SurveyTypeTranslations {
  [lang: string]: { name?: string };
}

export interface SurveyTypeEntity {
  id: number;
  name?: string;
  companyId?: number;
  baseSurveyTypeId?: number;
  baseSurveyType?: { id: number; name: string };
  categories: number[];
  translations?: SurveyTypeTranslations;
  [key: string]: unknown;
}

export type SurveyTypeState = Record<number, SurveyTypeEntity>;

interface MergePayload {
  entities?: {
    surveyTypes?: Record<number, Partial<SurveyTypeEntity> & { id: number; categories?: number[] | null }>;
  };
  result?: number | number[];
}

interface CreateCategoryPayload {
  surveyTypeId: number;
  result: number;
  entities?: unknown;
}

const defaultState: SurveyTypeState = {};

const reset = (): SurveyTypeState => defaultState;

/**
 * Iterate over each element of `action.payload.entities.surveyTypes` and merge each with any
 * existing entity with the same ID in the state (or create new one if none exist).
 */
const mergeSurveyTypes = (state: SurveyTypeState, action: PayloadAction<MergePayload | undefined>): void => {
  const surveyTypes = action.payload?.entities?.surveyTypes ?? {};

  Object.values(surveyTypes).forEach((surveyType) => {
    // If the server did not respond with any category information, the transformer
    // will have set `surveyType.categories` to `null`. Which indicates that we
    // should continue using the existing categories and replace them with an
    // empty array.
    const categories: number[] =
      surveyType.categories === null ? (state[surveyType.id]?.categories ?? []) : (surveyType.categories ?? []);

    state[surveyType.id] = {
      ...state[surveyType.id],
      ...surveyType,
      categories,
    };
  });
};

const mergeSurveyTypeCategories = (state: SurveyTypeState, action: PayloadAction<CreateCategoryPayload>): void => {
  const { surveyTypeId, result: newCategoryId } = action.payload;
  const categoryIds = [...state[surveyTypeId].categories, newCategoryId];
  state[surveyTypeId] = {
    ...state[surveyTypeId],
    categories: categoryIds,
  };
};

const surveyTypesReducer = createReducer(defaultState, (builder) => {
  builder.addCase(metaActions.reset, reset);
  builder.addCase(accountActions.logout.pending, reset);
  builder.addCase(accountActions.switchCompany.pending, reset);

  builder.addCase(surveyTypeActions.fetchSurveyTypesWithCategories.fulfilled, mergeSurveyTypes);
  builder.addCase(surveyTypeActions.createSurveyType.fulfilled, mergeSurveyTypes);
  builder.addCase(surveyTypeActions.updateSurveyType.fulfilled, mergeSurveyTypes);
  builder.addCase(categoryActions.createCategory.fulfilled, mergeSurveyTypeCategories);
});

export default surveyTypesReducer;
